<script lang="ts">
  /**
   * The report sheet: crowd size, then a rating per attribute.
   *
   * Star attributes get a radio group of stars; scale attributes get a slider between two
   * named ends (brief §7.1). The two are genuinely different questions and must not look the
   * same — a slider from "sehr jung" to "sehr alt" cannot be misread as a score.
   */
  import { type Attribute, CROWD_SIZES } from '$domain/attributes.ts';
  import { outbox } from './outbox.svelte.ts';
  import { describeError } from './session.svelte.ts';

  type Props = {
    attributes: Attribute[];
    position: { lat: number; lon: number; accuracy: number } | undefined;
    source: 'map' | 'quick' | 'passby';
    /** Pre-filled from the last report, which is what makes the quick flow two taps. */
    initial: Record<string, number>;
    onclose: () => void;
    onsaved: (ratings: Record<string, number>) => void;
  };

  const { attributes, position, source, initial, onclose, onsaved }: Props = $props();

  let crowd = $state<number>(CROWD_SIZES.few);
  // Both of these deliberately capture the value the sheet opened with rather than tracking
  // the prop. The sheet is mounted fresh on every open, so "initial" really is initial — and
  // making these derived would wipe what the user is halfway through typing the moment the
  // parent re-rendered.
  // svelte-ignore state_referenced_locally
  let ratings = $state<Record<string, number>>({ ...initial });
  let note = $state('');
  let busy = $state(false);
  let error = $state('');
  // svelte-ignore state_referenced_locally
  let expanded = $state(source === 'map');

  const CROWD_LABELS: Record<number, string> = {
    [CROWD_SIZES.single]: 'einzeln',
    [CROWD_SIZES.few]: 'ein paar',
    [CROWD_SIZES.many]: 'viele',
  };

  const starAttributes = $derived(attributes.filter((entry) => entry.kind === 'stars'));
  const scaleAttributes = $derived(attributes.filter((entry) => entry.kind === 'scale'));
  const hasStar = $derived(starAttributes.some((entry) => ratings[entry.key] !== undefined));

  function setRating(key: string, value: number) {
    // Tapping the same star again clears it: not rating an attribute is a different statement
    // from rating it zero, and there has to be a way back to "no opinion".
    ratings = ratings[key] === value ? omit(ratings, key) : { ...ratings, [key]: value };
  }

  function omit(source: Record<string, number>, key: string): Record<string, number> {
    const { [key]: _dropped, ...rest } = source;
    return rest;
  }

  async function save() {
    if (position === undefined) {
      error = 'Ohne Position geht es nicht — erlaube den Standortzugriff.';
      return;
    }
    busy = true;
    error = '';
    try {
      // Into the outbox first, then sent (brief §14a.3). The sheet closes as soon as the
      // report is durably queued rather than when the server has it — which is what makes
      // reporting from a cellar bar work, and what makes it feel instant everywhere else.
      await outbox.enqueue('sighting', {
        lat: position.lat,
        lon: position.lon,
        accuracy: position.accuracy,
        crowd,
        ratings,
        note: note.trim() === '' ? undefined : note.trim(),
        source,
        observedAt: new Date().toISOString(),
      });
      onsaved(ratings);
    } catch (cause) {
      // Only a storage failure reaches here — the network is the outbox's problem, not this
      // sheet's.
      error = describeError(cause);
    } finally {
      busy = false;
    }
  }
</script>

<div class="backdrop" role="presentation" onclick={onclose}></div>

<section class="sheet" aria-label="Fund melden">
  <header>
    <h2>Fund melden</h2>
    <button class="close" onclick={onclose} aria-label="Schließen">×</button>
  </header>

  <fieldset class="crowd">
    <legend>Wie viele?</legend>
    {#each Object.values(CROWD_SIZES) as size (size)}
      <button class:selected={crowd === size} onclick={() => (crowd = size)}>
        {CROWD_LABELS[size]}
      </button>
    {/each}
  </fieldset>

  {#each starAttributes as attribute (attribute.key)}
    <div class="row">
      <span class="label">{attribute.emoji} {attribute.label}</span>
      <div class="stars" role="radiogroup" aria-label={attribute.label}>
        {#each [1, 2, 3, 4, 5] as value (value)}
          <button
            role="radio"
            aria-checked={ratings[attribute.key] === value}
            aria-label={`${value} von 5`}
            class:on={(ratings[attribute.key] ?? 0) >= value}
            onclick={() => setRating(attribute.key, value)}
          >★</button>
        {/each}
      </div>
    </div>
  {/each}

  <button class="more" onclick={() => (expanded = !expanded)}>
    {expanded ? 'Weniger' : 'Mehr Angaben'}
  </button>

  {#if expanded}
    {#each scaleAttributes as attribute (attribute.key)}
      <div class="scale">
        <span class="label">{attribute.emoji} {attribute.label}</span>
        <input
          type="range"
          min="0"
          max="5"
          step="1"
          value={ratings[attribute.key] ?? 3}
          oninput={(event) =>
            (ratings = { ...ratings, [attribute.key]: Number(event.currentTarget.value) })}
          aria-label={attribute.label}
        />
        <div class="ends">
          <span>{attribute.ends?.[0]}</span><span>{attribute.ends?.[1]}</span>
        </div>
      </div>
    {/each}

    <label class="note">
      <span>Notiz</span>
      <input
        bind:value={note}
        maxlength="140"
        placeholder="Was war hier los? Keine Namen, keine Personenbeschreibungen."
      />
    </label>
  {/if}

  {#if error}<p class="error" role="alert">{error}</p>{/if}

  <button class="primary" disabled={busy || !hasStar} onclick={save}>
    {busy ? 'Speichere …' : 'Melden'}
  </button>
  {#if !hasStar}
    <p class="hint">Mindestens eine Sternebewertung, sonst sagt die Meldung nichts aus.</p>
  {/if}
</section>

<style>
  .backdrop {
    position: fixed;
    inset: 0;
    background: rgb(0 0 0 / 40%);
    z-index: 5;
  }

  .sheet {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 6;
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    max-height: 85vh;
    overflow-y: auto;
    padding: 1rem 1rem calc(1rem + env(safe-area-inset-bottom));
    border-radius: 1.25rem 1.25rem 0 0;
    background: var(--card);
    box-shadow: 0 -8px 32px rgb(0 0 0 / 25%);
  }

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  h2 {
    margin: 0;
    font-size: 1.1rem;
  }

  .close {
    border: 0;
    background: none;
    color: var(--muted);
    font-size: 1.75rem;
    line-height: 1;
    cursor: pointer;
  }

  fieldset {
    display: flex;
    gap: 0.5rem;
    margin: 0;
    padding: 0;
    border: 0;
  }

  legend {
    font-size: 0.8125rem;
    color: var(--muted);
  }

  .crowd button {
    flex: 1;
    padding: 0.5rem;
    border: 1px solid var(--border);
    border-radius: 0.6rem;
    background: var(--bg);
    color: var(--fg);
    cursor: pointer;
  }

  .crowd button.selected {
    border-color: var(--accent);
    background: color-mix(in srgb, var(--accent) 15%, transparent);
  }

  .row,
  .scale {
    display: grid;
    gap: 0.2rem;
  }

  .row {
    grid-template-columns: 1fr auto;
    align-items: center;
  }

  .label {
    font-size: 0.9375rem;
  }

  .stars button {
    border: 0;
    background: none;
    color: var(--border);
    font-size: 1.4rem;
    line-height: 1;
    padding: 0 0.1rem;
    cursor: pointer;
  }

  .stars button.on {
    color: #f5a524;
  }

  .ends {
    display: flex;
    justify-content: space-between;
    font-size: 0.75rem;
    color: var(--muted);
  }

  input[type='range'] {
    width: 100%;
    accent-color: var(--accent);
  }

  .note {
    display: grid;
    gap: 0.25rem;
    font-size: 0.8125rem;
    color: var(--muted);
  }

  .note input {
    padding: 0.6rem 0.7rem;
    border: 1px solid var(--border);
    border-radius: 0.6rem;
    background: var(--bg);
    color: var(--fg);
    font-size: 0.9375rem;
  }

  .more {
    align-self: flex-start;
    border: 0;
    background: none;
    color: var(--muted);
    font-size: 0.8125rem;
    text-decoration: underline;
    cursor: pointer;
  }

  .primary {
    padding: 0.85rem;
    border: 0;
    border-radius: 0.75rem;
    background: var(--accent);
    color: #fff;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
  }

  .primary:disabled {
    opacity: 0.5;
    cursor: default;
  }

  .hint,
  .error {
    margin: 0;
    font-size: 0.8125rem;
    color: var(--muted);
    text-align: center;
  }

  .error {
    color: var(--accent);
  }
</style>
